import axios from "axios";
import * as config from "@/config";
import Pusher from "pusher-js";
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { firebaseApp } from "..";
import { updateDeviceUpsertToken } from "./actionDispatcher";

export const getLoggedInUserToken = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  if (token && token.accessToken) return token;
};

export const isUserAuthenticated = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  axios.defaults.headers.common = {
    Authorization: `Bearer ${token?.accessToken}`,
    accountId: `${token?.account?.id}`,
    Accept: "application/json",
  };

  return token?.accessToken ? true : false;
};

let isRefreshing = false;
let failedRequestsQueue = [];

const processQueue = (error, token = null) => {
  failedRequestsQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedRequestsQueue = [];
};

const forceLogout = () => {
  localStorage.setItem("expiredTokenModal", true);
  localStorage.removeItem("token");
  window.location.href = "/";
};

const getStoredToken = () => {
  try {
    return JSON.parse(localStorage.getItem("token"));
  } catch {
    return null;
  }
};

const retryWithToken = (originalRequest, accessToken) => {
  originalRequest.headers.Authorization = `Bearer ${accessToken}`;
  return axios(originalRequest);
};

// Tabs share localStorage but not memory, so the refresh token is rotated
// across tabs through a Web Lock; falls back to a direct call without it.
const withRefreshLock = (callback) =>
  navigator?.locks?.request
    ? navigator.locks.request("token-refresh", callback)
    : callback();

const refreshAccessToken = async (staleRefreshToken) => {
  // Another tab may have rotated the token while this one waited for the lock.
  const stored = getStoredToken();
  if (stored?.refreshToken && stored.refreshToken !== staleRefreshToken) {
    return stored.accessToken;
  }

  const response = await axios.post(`${config.API_URL}refresh`, {
    refresh_token: staleRefreshToken,
  });
  const {
    accessToken,
    expiresAt,
    refreshToken: newRefreshToken,
  } = response?.data?.data || {};

  if (accessToken) {
    setUserToken({
      ...stored,
      accessToken,
      expiresAt,
      refreshToken: newRefreshToken,
    });
  }
  return accessToken;
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!isUserAuthenticated()) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    if (
      error?.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes("/refresh")
    ) {
      return Promise.reject(error);
    }

    if (!originalRequest?.headers?.Authorization) {
      return Promise.reject(error);
    }

    // The request went out with an older token than the one now stored, so
    // another tab already refreshed: retry instead of rotating again.
    const storedToken = getStoredToken();
    if (
      storedToken?.accessToken &&
      originalRequest.headers.Authorization !==
        `Bearer ${storedToken.accessToken}`
    ) {
      originalRequest._retry = true;
      return retryWithToken(originalRequest, storedToken.accessToken);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedRequestsQueue.push({ resolve, reject });
      }).then((newAccessToken) =>
        retryWithToken(originalRequest, newAccessToken),
      );
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const staleRefreshToken = storedToken?.refreshToken;

    try {
      if (!staleRefreshToken) {
        processQueue(new Error("No refresh token"), null);
        forceLogout();
        return Promise.reject(error);
      }

      const accessToken = await withRefreshLock(() =>
        refreshAccessToken(staleRefreshToken),
      );

      if (!accessToken) {
        processQueue(new Error("Refresh failed"), null);
        forceLogout();
        return Promise.reject(error);
      }

      processQueue(null, accessToken);
      return retryWithToken(originalRequest, accessToken);
    } catch (refreshError) {
      // A rejected refresh token may just mean another tab rotated it first.
      const latest = getStoredToken();
      if (
        latest?.accessToken &&
        latest?.refreshToken &&
        latest.refreshToken !== staleRefreshToken
      ) {
        processQueue(null, latest.accessToken);
        return retryWithToken(originalRequest, latest.accessToken);
      }
      processQueue(refreshError, null);
      forceLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export const isNotUser = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  let userRoleLevel = token?.user?.user_role?.level;
  return userRoleLevel < 4;
};

export const isSuperAdmin = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  let userRole = token?.user?.user_role?.slug;
  return userRole === "admin";
};

export const isAdministrator = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  let userRole = token?.user?.user_role?.slug;
  return userRole === "manager";
};

export const isReviewer = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  let userRole = token?.user?.user_role?.slug;
  return userRole === "curriculum-reviewer";
};
export const isAuthor = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  let userRole = token?.user?.user_role?.slug;
  return userRole === "curriculum-author";
};

export const isDistributor = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  let userRole = token?.user?.user_role?.slug;
  return userRole === "distributor";
};

export const isManager = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  let userRole = token?.user?.user_role?.slug;
  return userRole === "account-manager";
};

// Account switching overwrites token.account (the active org) and reloads, while
// token.user.account (the manager's home org) stays put. So the active org differs
// from the home org only after the user has switched into a managed organization.
export const isSwitchedAccount = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  const activeAccountId = +token?.account?.id;
  const homeAccountId = +token?.user?.account?.id;
  // Fail safe: if either id is missing/unparseable, treat as not switched so a
  // normal own-org manager is never wrongly locked out.
  if (!activeAccountId || !homeAccountId) return false;
  return activeAccountId !== homeAccountId;
};

// Account-manager who has switched into a managed org -> strictly read-only.
export const isManagerReadOnly = () => isManager() && isSwitchedAccount();

// Account-manager operating inside its own org -> gains user-level write access.
export const isManagerOwnOrg = () => isManager() && !isSwitchedAccount();

// Account-managers and form-administrators can only view existing reports.
export const canAddReport = () => isNotUser() && !isManager() && !isFormAdmin();

export const isCurriculumAdmin = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  let userRole = token?.user?.user_role?.slug;
  return userRole === "curriculum-admin";
};

export const isFormAdmin = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  let userRole = token?.user?.user_role?.slug;
  return userRole === "form-administrator";
};

export const isServiceTechnician = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  let userRole = token?.user?.account?.is_service_account;
  //&& token?.user?.user_role?.slug === 'manager' ;
  return userRole;
};

export const isServiceTechnicianRole = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  let userRole = token?.user?.is_service_technician;
  return userRole;
};

export const isUser = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  let userRole = token?.user?.user_role?.slug;
  return userRole === "user";
};

export const authHeader = () => {
  let token = JSON.parse(localStorage.getItem("token"));
  return token && token.accessToken
    ? { Authorization: "Bearer " + token.accessToken }
    : {};
};

export const getAccountId = () => {
  let data = JSON.parse(localStorage.getItem("token"));
  return data?.account?.id;
};

export const getTimeZoneId = () => {
  let data = JSON.parse(localStorage.getItem("token"));
  return (
    parseInt(data?.account?.timezone_id) || parseInt(data?.user?.timezone?.id)
  );
};

export const setUserToken = (data) => {
  data.account = data?.account || data?.user?.account;
  localStorage.setItem("token", JSON.stringify(data));

  if (data && data.accessToken)
    // axios.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`
    axios.defaults.headers.common = {
      Authorization: `Bearer ${data?.accessToken}`,
      accountId: `${data?.account?.id}`,
      Accept: "application/json",
    };
};

export const pusher = new Pusher(config.PUSHER_APP_KEY, {
  cluster: config.PUSHER_APP_CLUSTER || "mt1",
  // Always TLS: the app is served over HTTPS, so a ws:// socket would be
  // blocked as mixed content and silently demoted to the HTTP transports.
  forceTLS: true,
  // Pusher's HTTP fallback transports register a window "unload" listener,
  // which Chrome now refuses under its unload deprecation. WebSocket-only
  // keeps that code path from ever running.
  enabledTransports: ["ws", "wss"],
});

// export const activityChannel = pusher.subscribe("activity");

export const shortenString = (data) => {
  if (data?.length > 0) {
    let newText = data;
    if (data.length > 50) {
      return newText.slice(0, 50) + "...";
    } else {
      return data;
    }
  }
};

export async function requestPermission() {
  if (
    "serviceWorker" in navigator &&
    typeof Notification !== "undefined" &&
    (await isSupported())
  ) {
    const messaging = getMessaging(firebaseApp);
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      try {
        const token = await getToken(messaging, {
          vapidKey: config.VAPID_KEY,
        });
        let data = {
          type: "web",
          token: token,
          status: "active",
        };
        updateDeviceUpsertToken(data);
      } catch (e) {}
    }
  }
}
